/*
 * Layout storage backends.
 *
 * The editor and the VR page talk to one of these:
 *   DeviceBackend — signed out: layouts in this browser's localStorage
 *                   (device-only; nothing leaves the machine)
 *   CloudBackend  — signed in: layouts in Firestore under the user's account
 *                   (see cloud.ts; realtime across devices)
 *
 * Docs are stored as JSON strings (runtime `_` keys already stripped by the
 * caller); mtime is unix seconds.
 */
import type { Doc } from './types.ts';
import { MAX_VERSIONS, prune } from './versions.ts';

export interface LayoutMeta { name: string; mtime: number }
/** A kept snapshot of a layout; id is backend-specific, mtime = when that state was saved. */
export interface VersionMeta { id: string; mtime: number }
export interface LoadResult { doc: Doc; mtime: number }

export interface StoreBackend {
  /** 'device' = this browser only · 'cloud' = the signed-in account */
  kind: 'device' | 'cloud';
  /** Most recently edited first. */
  list(): Promise<LayoutMeta[]>;
  load(name: string): Promise<LoadResult | null>;
  /** Returns the new mtime (unix seconds). */
  /** mtime (unix seconds) may be provided by the caller so it can record it
      BEFORE the write — the write's own watch echo then compares equal
      instead of looking like a remote change. */
  save(name: string, json: string, mtime?: number): Promise<number>;
  remove(name: string): Promise<void>;
  /** Live list updates (other tabs / other devices / Claude). Returns unsubscribe. */
  watchList(cb: (list: LayoutMeta[]) => void): () => void;
  /** Version history, newest first (see versions.ts for the policy). */
  listVersions(name: string): Promise<VersionMeta[]>;
  loadVersion(name: string, id: string): Promise<LoadResult | null>;
  /** Keep a snapshot of `json` as the layout stood at `mtime`; prunes to MAX_VERSIONS. */
  saveVersion(name: string, json: string, mtime: number): Promise<void>;
}

/** Seed an empty device store so first-run isn't a blank app: from the legacy
    layouts/ folder on the local dev server, else from the site's bundled
    starter layouts. Shared by the editor and the VR page. */
export async function seedDeviceStore(dev: StoreBackend): Promise<void> {
  if ((await dev.list()).length) return;
  const strip = (k: string, v: unknown) => k.startsWith('_') ? undefined : v;
  try {
    const res = await fetch('/api/layouts');
    if (res.ok) {
      for (const { name } of await res.json()) {
        const r = await fetch('/api/layouts/' + encodeURIComponent(name));
        if (r.ok) await dev.save(name, JSON.stringify(await r.json(), strip));
      }
      return;
    }
  } catch { /* no legacy API here (hosted site) */ }
  try {
    const res = await fetch('/starter-layouts.json');
    if (!res.ok) return;
    for (const { name, doc } of await res.json())
      await dev.save(name, JSON.stringify(doc, strip));
  } catch { /* fine: start empty */ }
}

const PREFIX = 'playout:layout:';
const VPREFIX = 'playout:version:';   // + encodeURIComponent(name) + ':' + id
const byMtimeDesc = (a: LayoutMeta, b: LayoutMeta) => b.mtime - a.mtime || a.name.localeCompare(b.name);

export class DeviceBackend implements StoreBackend {
  kind = 'device' as const;

  private read(name: string): { m: number; d: string } | null {
    const raw = localStorage.getItem(PREFIX + name);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  async list(): Promise<LayoutMeta[]> {
    const out: LayoutMeta[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)!;
      if (!k.startsWith(PREFIX)) continue;
      const rec = this.read(k.slice(PREFIX.length));
      if (rec) out.push({ name: k.slice(PREFIX.length), mtime: rec.m });
    }
    return out.sort(byMtimeDesc);
  }

  async load(name: string): Promise<LoadResult | null> {
    const rec = this.read(name);
    if (!rec) return null;
    try { return { doc: JSON.parse(rec.d), mtime: rec.m }; } catch { return null; }
  }

  async save(name: string, json: string, mtime?: number): Promise<number> {
    const m = mtime ?? Date.now() / 1000;
    localStorage.setItem(PREFIX + name, JSON.stringify({ m, d: json }));
    return m;
  }

  async remove(name: string): Promise<void> {
    localStorage.removeItem(PREFIX + name);
    for (const v of await this.listVersions(name)) localStorage.removeItem(this.vkey(name, v.id));
  }

  private vkey(name: string, id: string): string { return VPREFIX + encodeURIComponent(name) + ':' + id; }

  async listVersions(name: string): Promise<VersionMeta[]> {
    const head = VPREFIX + encodeURIComponent(name) + ':';
    const out: VersionMeta[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)!;
      if (!k.startsWith(head)) continue;
      try { out.push({ id: k.slice(head.length), mtime: JSON.parse(localStorage.getItem(k)!).m }); } catch { /* skip junk */ }
    }
    return out.sort((a, b) => b.mtime - a.mtime);
  }

  async loadVersion(name: string, id: string): Promise<LoadResult | null> {
    const raw = localStorage.getItem(this.vkey(name, id));
    if (!raw) return null;
    try { const rec = JSON.parse(raw); return { doc: JSON.parse(rec.d), mtime: rec.m }; } catch { return null; }
  }

  async saveVersion(name: string, json: string, mtime: number): Promise<void> {
    localStorage.setItem(this.vkey(name, String(Math.round(mtime * 1000))), JSON.stringify({ m: mtime, d: json }));
    for (const v of prune(await this.listVersions(name), MAX_VERSIONS).drop) localStorage.removeItem(this.vkey(name, v.id));
  }

  watchList(cb: (list: LayoutMeta[]) => void): () => void {
    // localStorage has no same-tab events; poll cheaply + storage event for other tabs
    let lastSig = '';
    const check = async () => {
      const list = await this.list();
      const sig = JSON.stringify(list);
      if (sig !== lastSig) { lastSig = sig; cb(list); }
    };
    const iv = setInterval(check, 2500);
    const onStorage = (e: StorageEvent) => { if (!e.key || e.key.startsWith(PREFIX)) check(); };
    addEventListener('storage', onStorage);
    check();
    return () => { clearInterval(iv); removeEventListener('storage', onStorage); };
  }
}
